import {
  Badge,
  Box,
  Button,
  CloseButton,
  Divider,
  Flex,
  Grid,
  Group,
  rem,
  Select,
  Text,
  TextInput,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import React, { forwardRef, useEffect, useRef, useState } from "react";
import swal from "sweetalert";
import { useSelector } from "react-redux";
import { forgetPassword, getAllFacilities } from "../../services";
import { removeStaff } from "../../services/staff";
import useStyles from "./style";
import ActionButtons from "./ActionButtons";
import { RiDeleteBin7Line } from "react-icons/ri";
import { useNavigate } from "react-router-dom";
import { DropDownArrowSvg } from "../Svgs/DropDownArrowSvg";
/**
 * StaffDetailsTab component
 *
 * This component is used to display and edit staff details. It
 * includes a form with fields for first name, last name, job title,
 * phone number, email, and facility. It also includes a component
 * for adding and removing facilities.
 *
 * @param {object} data - The staff data object
 * @param {string} mode - The mode of the component, either "add", "edit", or "view"
 * @param {object} form - The form object from Mantine's useForm hook
 * @param {function} setSelectedStaff - A function to set the selected staff data object
 * @param {function} setAddedFacilities - A function to set the added facilities array
 * @param {array} addedFacilities - The added facilities array
 * @param {function} handleSave - A function to handle the form save action
 * @param {function} handleCancel - A function to handle the form cancel action
 */
export const StaffDetailsTab = ({
  data,
  mode,
  form,
  setSelectedStaff,
  handleSave,
  handleCancel,
}) => {
  const { classes } = useStyles();
  const isMobile = useMediaQuery("(max-width: 600px)");
  const navigate = useNavigate();

  const resetPassword = async () => {
    const email = await swal({
      title: `Reset Password`,
      text: "Do you want to email reset password link?",
      dangerMode: false,
      buttons: ["Cancel", "Send"],
    });
    if (email) {
      forgetPassword({ email: data?.email }, "reset");
    }
  };
  const deleteStaffHandle = async () => {
    const willDelete = await swal({
      title: `Are you sure?`,
      text: "This action cannot be undone.",
      icon: "warning",
      dangerMode: true,
      buttons: ["Cancel", "Delete"],
    });
    if (willDelete) {
      removeStaff(data?.id, () => {}, null);
      navigate("/home/staff");
    }
  };

  const [search, setSearch] = useState("");

  return (
    <Box className={classes.StaffDetailsTab}>
      {mode !== "add" && (
        <>
          <Flex justify={"flex-end"}>
            <Button
              className={classes.password}
              variant="transparent"
              onClick={resetPassword}
            >
              Reset Password?
            </Button>
            <Button
              className={classes.delete}
              variant="transparent"
              color="gray"
              c={"red"}
              onClick={deleteStaffHandle}
            >
              <RiDeleteBin7Line size={18} />
              <span style={{ marginLeft: "5px" }}>Delete</span>
            </Button>
          </Flex>
          <Divider my={10} />
        </>
      )}
      <form onSubmit={form.onSubmit(handleSave)} className={classes.formBox}>
        <Grid mt={10}>
          <Grid.Col span={isMobile ? 12 : 6}>
            <TextInput
              label="First Name"
              placeholder="Enter First Name"
              radius="md"
              withAsterisk
              disabled={mode === "view"}
              {...form.getInputProps("firstName")}
              onChange={(e) => {
                form.setFieldValue("firstName", e?.target?.value);
                setSelectedStaff((prev) => {
                  return { ...prev, firstName: e?.target?.value };
                });
              }}
            />
          </Grid.Col>
          <Grid.Col span={isMobile ? 12 : 6}>
            <TextInput
              label="Last Name"
              placeholder="Enter Last Name"
              radius="md"
              withAsterisk
              disabled={mode === "view"}
              {...form.getInputProps("lastName")}
              onChange={(e) => {
                form.setFieldValue("lastName", e?.target?.value);
                setSelectedStaff((prev) => {
                  return { ...prev, lastName: e?.target?.value };
                });
              }}
            />
          </Grid.Col>
          <Grid.Col span={isMobile ? 12 : 6}>
            <TextInput
              label="Job Title"
              placeholder="Enter Job Title"
              type="text"
              radius="md"
              withAsterisk
              disabled={mode === "view"}
              {...form.getInputProps("position")}
            />
          </Grid.Col>
          <Grid.Col span={isMobile ? 12 : 6}>
            <TextInput
              label="Phone Number"
              placeholder="Enter Phone Number"
              type="number"
              radius="md"
              disabled={mode === "view"}
              {...form.getInputProps("phone")}
            />
          </Grid.Col>
          <Grid.Col span={isMobile ? 12 : 12}>
            <TextInput
              label="Email"
              placeholder="Enter Email"
              type="text"
              radius="md"
              withAsterisk
              disabled={mode === "view"}
              {...form.getInputProps("email")}
            />
          </Grid.Col>
        </Grid>
        {(mode === "add" || mode === "edit") && (
          <>
            <Divider my={10} />
            <Box pt={20}>
              <ActionButtons
                onCancel={handleCancel}
                type="submit"
                onSave={handleSave}
              />
            </Box>
          </>
        )}
      </form>
    </Box>
  );
};
